import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { BarChart3, Download, FileText, Calendar, Euro, TrendingUp, Filter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getWeekNumber(d) {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
}

export default function SalesReport() {
  const [guides, setGuides] = useState([]);
  const [filter, setFilter] = useState("month"); // day, week, month, year, custom
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sales-guides`);
      setGuides(res.data.filter(g => g.status === "emitida"));
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Filter logic
  const filteredGuides = guides.filter(g => {
    if (!g.created_at) return false;
    const d = new Date(g.created_at);
    const now = new Date();
    
    switch (filter) {
      case "day":
        return d.toDateString() === now.toDateString();
      case "week":
        return getWeekNumber(d) === getWeekNumber(now) && d.getFullYear() === now.getFullYear();
      case "month":
        return d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
      case "year":
        return d.getFullYear() === selectedYear;
      case "all":
        return true;
      case "custom":
        if (!customStart || !customEnd) return true;
        const start = new Date(customStart);
        const end = new Date(customEnd);
        end.setHours(23, 59, 59);
        return d >= start && d <= end;
      default:
        return true;
    }
  });

  // Group by period
  const groupedByClient = {};
  filteredGuides.forEach(g => {
    const key = g.client_name || "Sem cliente";
    if (!groupedByClient[key]) groupedByClient[key] = { guides: [], total: 0, totalWithIva: 0 };
    groupedByClient[key].guides.push(g);
    groupedByClient[key].total += (g.subtotal || g.total_value || 0);
    groupedByClient[key].totalWithIva += (g.total_value_with_iva || g.total_value || 0);
  });

  // Totals
  const totalSubtotal = filteredGuides.reduce((s, g) => s + (g.subtotal || g.total_value || 0), 0);
  const totalIva = filteredGuides.reduce((s, g) => s + (g.iva_value || 0), 0);
  const totalWithIva = filteredGuides.reduce((s, g) => s + (g.total_value_with_iva || g.total_value || 0), 0);
  const totalItems = filteredGuides.reduce((s, g) => s + (g.items?.length || 0), 0);

  const months = ["Janeiro", "Fevereiro", "Marco", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

  // Monthly breakdown for year view
  const monthlyData = Array.from({ length: 12 }, (_, i) => {
    const monthGuides = guides.filter(g => {
      if (!g.created_at) return false;
      const d = new Date(g.created_at);
      return d.getMonth() === i && d.getFullYear() === selectedYear;
    });
    return {
      month: months[i],
      count: monthGuides.length,
      total: monthGuides.reduce((s, g) => s + (g.total_value_with_iva || g.total_value || 0), 0),
    };
  });

  const exportReportPDF = () => {
    const win = window.open("", "_blank");
    const filterLabel = filter === "day" ? "Hoje" : filter === "week" ? "Esta Semana" : filter === "month" ? `${months[selectedMonth]} ${selectedYear}` : filter === "year" ? `Ano ${selectedYear}` : filter === "custom" ? `${customStart} a ${customEnd}` : "Todas";
    
    const rowsHtml = filteredGuides.map(g => `
      <tr>
        <td style="border:1px solid #ddd;padding:6px;font-weight:600;">${g.number}</td>
        <td style="border:1px solid #ddd;padding:6px;">${new Date(g.created_at).toLocaleDateString("pt-PT")}</td>
        <td style="border:1px solid #ddd;padding:6px;">${g.client_name}</td>
        <td style="border:1px solid #ddd;padding:6px;">${g.client_nif || "-"}</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">${(g.subtotal || g.total_value || 0).toFixed(2)} &euro;</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;">${(g.iva_value || 0).toFixed(2)} &euro;</td>
        <td style="border:1px solid #ddd;padding:6px;text-align:right;font-weight:700;">${(g.total_value_with_iva || g.total_value || 0).toFixed(2)} &euro;</td>
      </tr>`).join("");

    const clientSummary = Object.entries(groupedByClient).map(([name, data]) => `
      <tr><td style="border:1px solid #ddd;padding:6px;font-weight:600;">${name}</td>
      <td style="border:1px solid #ddd;padding:6px;text-align:center;">${data.guides.length}</td>
      <td style="border:1px solid #ddd;padding:6px;text-align:right;font-weight:700;">${data.totalWithIva.toFixed(2)} &euro;</td></tr>`).join("");

    win.document.write(`<!DOCTYPE html><html><head><title>Relatorio Vendas</title>
      <style>body{font-family:'Segoe UI',sans-serif;padding:30px;max-width:900px;margin:0 auto;font-size:12px}
      h1{color:#1a365d;font-size:20px;margin:0} h2{color:#334155;font-size:14px;margin:20px 0 10px}
      .header{border-bottom:3px solid #1a365d;padding-bottom:15px;margin-bottom:20px;display:flex;justify-content:space-between;align-items:center}
      .period{background:#f0f9ff;border:1px solid #bae6fd;padding:8px 16px;border-radius:6px;font-weight:600;color:#0369a1}
      .kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:20px}
      .kpi{background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px;text-align:center}
      .kpi .val{font-size:22px;font-weight:700;color:#1a365d}
      .kpi .lbl{font-size:10px;color:#64748b;text-transform:uppercase}
      table{width:100%;border-collapse:collapse;margin-bottom:20px}
      th{background:#1a365d;color:white;padding:8px;text-align:left;font-size:10px;text-transform:uppercase}
      .totals{background:#f0f9ff;font-weight:700}
      @media print{body{padding:15px}}
      </style></head><body>
      <div class="header">
        <div><img src="/ep3d-logo.png" style="height:40px" onerror="this.style.display='none'"><h1>Relatorio de Vendas</h1></div>
        <div class="period">${filterLabel}</div>
      </div>
      <div class="kpis">
        <div class="kpi"><div class="val">${filteredGuides.length}</div><div class="lbl">Guias</div></div>
        <div class="kpi"><div class="val">${totalSubtotal.toFixed(2)} &euro;</div><div class="lbl">Subtotal</div></div>
        <div class="kpi"><div class="val">${totalIva.toFixed(2)} &euro;</div><div class="lbl">IVA</div></div>
        <div class="kpi"><div class="val">${totalWithIva.toFixed(2)} &euro;</div><div class="lbl">Total c/ IVA</div></div>
      </div>
      <h2>Resumo por Cliente</h2>
      <table><thead><tr><th>Cliente</th><th style="text-align:center">Guias</th><th style="text-align:right">Total</th></tr></thead><tbody>${clientSummary}</tbody></table>
      <h2>Detalhe das Guias</h2>
      <table><thead><tr><th>Numero</th><th>Data</th><th>Cliente</th><th>NIF</th><th style="text-align:right">Subtotal</th><th style="text-align:right">IVA</th><th style="text-align:right">Total</th></tr></thead>
      <tbody>${rowsHtml}
        <tr class="totals"><td colspan="4" style="border:1px solid #ddd;padding:8px;text-align:right;">TOTAIS:</td>
        <td style="border:1px solid #ddd;padding:8px;text-align:right;">${totalSubtotal.toFixed(2)} &euro;</td>
        <td style="border:1px solid #ddd;padding:8px;text-align:right;">${totalIva.toFixed(2)} &euro;</td>
        <td style="border:1px solid #ddd;padding:8px;text-align:right;">${totalWithIva.toFixed(2)} &euro;</td></tr>
      </tbody></table>
      <p style="text-align:center;color:#999;font-size:10px;margin-top:30px">Gerado em ${new Date().toLocaleString("pt-PT")} | EP3D MES</p>
    </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  return (
    <div className="space-y-6" data-testid="sales-report-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Relatorio de Vendas</h1>
          <p className="text-gray-500 mt-1">Analise de vendas por periodo</p>
        </div>
        <Button onClick={exportReportPDF} data-testid="export-report-btn">
          <Download className="h-4 w-4 mr-2" /> Exportar PDF
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-600">Periodo:</span>
            {[
              { id: "day", label: "Hoje" },
              { id: "week", label: "Esta Semana" },
              { id: "month", label: "Mes" },
              { id: "year", label: "Ano" },
              { id: "all", label: "Tudo" },
              { id: "custom", label: "Personalizado" },
            ].map(f => (
              <Button key={f.id} variant={filter === f.id ? "default" : "outline"} size="sm" onClick={() => setFilter(f.id)}>{f.label}</Button>
            ))}

            {(filter === "month" || filter === "year") && (
              <select className="border rounded-lg px-3 py-1.5 text-sm ml-4" value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))}>
                {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
            {filter === "month" && (
              <select className="border rounded-lg px-3 py-1.5 text-sm" value={selectedMonth} onChange={e => setSelectedMonth(parseInt(e.target.value))}>
                {months.map((m, i) => <option key={i} value={i}>{m}</option>)}
              </select>
            )}
            {filter === "custom" && (
              <>
                <input type="date" className="border rounded-lg px-3 py-1.5 text-sm ml-4" value={customStart} onChange={e => setCustomStart(e.target.value)} />
                <span className="text-gray-400">a</span>
                <input type="date" className="border rounded-lg px-3 py-1.5 text-sm" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="p-5 text-center"><Euro className="h-6 w-6 mx-auto mb-2 text-green-500" /><p className="text-xs text-gray-500 uppercase">Total Vendas (c/ IVA)</p><p className="text-3xl font-bold text-green-700">{totalWithIva.toFixed(2)} &euro;</p></CardContent></Card>
        <Card><CardContent className="p-5 text-center"><FileText className="h-6 w-6 mx-auto mb-2 text-blue-500" /><p className="text-xs text-gray-500 uppercase">Guias Emitidas</p><p className="text-3xl font-bold text-blue-700">{filteredGuides.length}</p></CardContent></Card>
        <Card><CardContent className="p-5 text-center"><TrendingUp className="h-6 w-6 mx-auto mb-2 text-purple-500" /><p className="text-xs text-gray-500 uppercase">Subtotal (s/ IVA)</p><p className="text-3xl font-bold text-purple-700">{totalSubtotal.toFixed(2)} &euro;</p></CardContent></Card>
        <Card><CardContent className="p-5 text-center"><BarChart3 className="h-6 w-6 mx-auto mb-2 text-amber-500" /><p className="text-xs text-gray-500 uppercase">IVA Liquidado</p><p className="text-3xl font-bold text-amber-700">{totalIva.toFixed(2)} &euro;</p></CardContent></Card>
      </div>

      {/* Monthly chart for year view */}
      {filter === "year" && (
        <Card>
          <CardHeader><CardTitle>Vendas Mensais - {selectedYear}</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-48">
              {monthlyData.map((m, i) => {
                const maxVal = Math.max(...monthlyData.map(x => x.total), 1);
                const height = (m.total / maxVal) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center">
                    <span className="text-xs text-gray-500 mb-1">{m.total > 0 ? `${m.total.toFixed(0)}` : ""}</span>
                    <div className="w-full bg-blue-500 rounded-t" style={{ height: `${Math.max(height, 2)}%`, minHeight: "4px" }}></div>
                    <span className="text-xs text-gray-600 mt-1 font-medium">{m.month.slice(0, 3)}</span>
                    <span className="text-xs text-gray-400">{m.count > 0 ? m.count : ""}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* By client */}
      <Card>
        <CardHeader><CardTitle>Resumo por Cliente</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead className="text-center">Guias</TableHead>
                <TableHead className="text-right">Subtotal</TableHead>
                <TableHead className="text-right">Total c/ IVA</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(groupedByClient).sort((a, b) => b[1].totalWithIva - a[1].totalWithIva).map(([name, data]) => (
                <TableRow key={name}>
                  <TableCell className="font-medium">{name}</TableCell>
                  <TableCell className="text-center">{data.guides.length}</TableCell>
                  <TableCell className="text-right">{data.total.toFixed(2)} &euro;</TableCell>
                  <TableCell className="text-right font-bold">{data.totalWithIva.toFixed(2)} &euro;</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detailed list */}
      <Card>
        <CardHeader><CardTitle>Detalhe das Guias ({filteredGuides.length})</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numero</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>NIF</TableHead>
                <TableHead>Artigos</TableHead>
                <TableHead className="text-right">Subtotal</TableHead>
                <TableHead className="text-right">IVA</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Pagamento</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredGuides.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center text-gray-400 py-8">Nenhuma guia neste periodo</TableCell></TableRow>
              ) : filteredGuides.map(g => (
                <TableRow key={g.id}>
                  <TableCell className="font-mono font-bold">{g.number}</TableCell>
                  <TableCell>{new Date(g.created_at).toLocaleDateString("pt-PT")}</TableCell>
                  <TableCell className="font-medium">{g.client_name}</TableCell>
                  <TableCell className="text-gray-500">{g.client_nif || "-"}</TableCell>
                  <TableCell>{g.items?.length || 0} ref.</TableCell>
                  <TableCell className="text-right">{(g.subtotal || g.total_value || 0).toFixed(2)} &euro;</TableCell>
                  <TableCell className="text-right">{(g.iva_value || 0).toFixed(2)} &euro;</TableCell>
                  <TableCell className="text-right font-bold">{(g.total_value_with_iva || g.total_value || 0).toFixed(2)} &euro;</TableCell>
                  <TableCell className="text-xs text-gray-500">{g.payment_method || "-"}</TableCell>
                </TableRow>
              ))}
              {filteredGuides.length > 0 && (
                <TableRow className="bg-slate-50 font-bold">
                  <TableCell colSpan={5}>TOTAIS</TableCell>
                  <TableCell className="text-right">{totalSubtotal.toFixed(2)} &euro;</TableCell>
                  <TableCell className="text-right">{totalIva.toFixed(2)} &euro;</TableCell>
                  <TableCell className="text-right text-green-700">{totalWithIva.toFixed(2)} &euro;</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
